import { Fragment, FC, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, SxStyleProp, Text, Flex, Close } from 'theme-ui';
import { Theme } from 'theme';
import { isNil } from 'ramda';

type ModalBasicProps = {
  children: ReactNode;
  opened?: boolean;
  closeHandler?: () => void;
};

export type ModalProps = ModalBasicProps & {
  title?: ReactNode;
  color: keyof Theme['colors'];
  dataTest?: string;
};

const OVERLAY_DATA_NAME = 'overlay';

const DOM_CONTAINER = document.createElement('div');
document.body.appendChild(DOM_CONTAINER);

const PADDING = 4;

const overlayStyles: SxStyleProp = {
  position: 'fixed',
  top: 0,
  left: 0,
  overflow: 'hidden',
  display: 'flex',
  zIndex: 'modal',
  width: '100%',
  height: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
  bg: 'overlay'
};

const modalContainerStyles: SxStyleProp = {
  width: theme => `calc(100% - ${theme.space[PADDING] * 2}px)`,
  maxWidth: 1052,
  justifyContent: 'center'
};

const contentContainerStyles: SxStyleProp = {
  position: 'relative',
  borderTop: '4px solid',
  maxWidth: '100%',
  m: 'auto',
  px: 7,
  py: 6,
  bg: 'background',
  borderRadius: 'modal',
  textAlign: 'center',
  color: 'grey4'
};

const Overlay: FC<{ children: ReactNode; onClick: () => void }> = ({ onClick, children }) => (
  <Box
    data-name={OVERLAY_DATA_NAME}
    sx={{ ...overlayStyles }}
    onClick={(e: any) => {
      if (onClick && e.target.dataset.name === OVERLAY_DATA_NAME) {
        onClick();
      }
    }}
  >
    {children}
  </Box>
);

export const isModalOpened = (): boolean =>
  !isNil(DOM_CONTAINER.querySelector('[data-test=modal]')) ||
  !isNil(document.querySelector('[data-test=checkout-modal]'));

const Portal: FC<{ children: ReactNode }> = ({ children }) => {
  const container = useRef(document.createElement('div'));

  useEffect(() => {
    DOM_CONTAINER.appendChild(container.current);
    return () => {
      DOM_CONTAINER.removeChild(container.current);
    };
  }, []);

  return createPortal(children, container.current);
};

const Modal: FC<ModalProps> = ({ title = '', children, closeHandler, opened, color, dataTest }) => {
  return (
    opened && (
      <Portal>
        <Overlay onClick={closeHandler}>
          <Flex sx={modalContainerStyles} data-test={dataTest}>
            <Box sx={{ ...contentContainerStyles, borderColor: color }} data-test="modal">
              {closeHandler && !title && (
                <Close
                  sx={{ position: 'absolute', top: 4, right: 5 }}
                  onClick={closeHandler}
                  data-test="close"
                />
              )}
              {title !== '' && (
                <Flex
                  sx={{
                    justifyContent: closeHandler ? 'space-between' : 'center',
                    alignItems: 'center',
                    mt: 3,
                    mb: 6
                  }}
                >
                  {closeHandler ? (
                    <Fragment>
                      <Text sx={{ fontSize: 3, fontWeight: 'body' }}>{title}</Text>
                      <Close ml={4} onClick={closeHandler} />
                    </Fragment>
                  ) : (
                    <Text variant="popupHeading">{title}</Text>
                  )}
                </Flex>
              )}
              {children}
            </Box>
          </Flex>
        </Overlay>
      </Portal>
    )
  );
};

export default Modal;

export const ModalBasic = ({ opened, closeHandler, children }: ModalBasicProps) => {
  return (
    opened && (
      <Portal>
        <Overlay onClick={closeHandler}>{children}</Overlay>
      </Portal>
    )
  );
};
